import { Fragment, ReactNode, isValidElement, Dispatch, SetStateAction } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle } from './drag-handle';
import { DataTableColumn } from '../table/data-table';
import { Checkbox } from '../checkbox/checkbox';
import { Typography } from '../typography/typography';
import { QuipliLink } from '../link/quipli-link';
import classNames from 'classnames';

export const DraggableRow = ({
  columnNames,
  selectedItems,
  setSelectedItems,
  id,
  idx,
  className,
  fields,
  redirectTo,
  extraRow,
}: {
  columnNames: DataTableColumn[];
  selectedItems: Array<number>;
  setSelectedItems: Dispatch<SetStateAction<number[]>>;
  id: number;
  idx: number;
  className: string | undefined;
  fields: Record<string, JSX.Element | ReactNode>;
  redirectTo: string | undefined;
  extraRow?: {
    content: string | JSX.Element | ReactNode;
    className?: string;
  };
}) => {
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };
  return (
    <Fragment key={`tr-wrapper-${idx}`}>
      <tr
        ref={setNodeRef}
        className={classNames(className, { '!border-b-0 [&>td]:!border-b-0': !!extraRow })}
        style={style}
      >
        {isDragging ? (
          <td colSpan={columnNames.length}>&nbsp;</td>
        ) : (
          columnNames.map(
            ({ key, fixedAsFirst, fixedAsLast, isCheckBoxColumn, hide = false, showRule = 'Both', className }) => {
              let tdContent = (
                <>
                  {isCheckBoxColumn && (
                    <Checkbox
                      type="white"
                      isChecked={selectedItems.includes(idx)}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setSelectedItems((prevState: Array<number>) => [...prevState, idx]);
                        } else {
                          setSelectedItems((prevState: Array<number>) =>
                            prevState.filter((elem: number) => elem !== idx),
                          );
                        }
                      }}
                    />
                  )}

                  {isValidElement(fields[key]) ? (
                    fields[key]
                  ) : (
                    <Typography type="text-sm" className="text-quipli-black-800">
                      {fields[key]}
                    </Typography>
                  )}
                </>
              );

              if (redirectTo && !fixedAsFirst && !fixedAsLast) {
                tdContent = <QuipliLink to={redirectTo}>{tdContent}</QuipliLink>;
              }

              const td = !hide && showRule !== 'OnlyMobile' && (
                <td
                  key={`td-${id}-${key}`}
                  className={classNames(key, className, {
                    hasCheckBox: isCheckBoxColumn,
                    'sticky-col-first': fixedAsFirst,
                    'sticky-col-last': fixedAsLast,
                  })}
                >
                  {key == 'sort' ? <DragHandle attributes={attributes} listeners={listeners} /> : tdContent}
                </td>
              );

              return td;
            },
          )
        )}
      </tr>
      {!!extraRow && (
        <tr key={`tr-${idx}-extra`}>
          <td colSpan={columnNames.length} className={classNames(extraRow.className)}>
            {extraRow.content}
          </td>
        </tr>
      )}
    </Fragment>
  );
};
